"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "sonner";
import {
  X,
  User,
  Phone,
  Mail,
  Calendar,
  Users,
  BedDouble,
  MessageSquare,
  Send,
  RotateCw,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { useAppDispatch } from "@/store/hooks";
import { insertInquiry } from "@/services/home.services";
import { parseQueryParams } from "@/_helper/helper";

export interface OfferInquiryFormProps {
  offerTitle: string;
  offerId?: number | string;
  isModal?: boolean;
  onClose?: () => void;
  className?: string;
}

interface IOfferInquiryFormValues {
  FirstName: string;
  MobileNo: string;
  Email: string;
  Room: string;
  Adults: string;
  CheckIn: string;
  CheckOut: string;
  Remarks: string;
  CaptchaInput: string;
  PropertyURL: string;
}

export const OfferInquiryForm: React.FC<OfferInquiryFormProps> = ({
  offerTitle,
  offerId,
  isModal = false,
  onClose,
  className = "",
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [isSuccess, setIsSuccess] = useState(false);
  const [captchaCode, setCaptchaCode] = useState<string>("");
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const formikRef = useRef<any>(null);

  const generateCaptcha = useCallback(() => {
    const chars = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
    let code = "";
    for (let i = 0; i < 5; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(code);
    if (formikRef.current) {
      formikRef.current.setFieldValue("CaptchaInput", "");
      formikRef.current.setFieldTouched("CaptchaInput", false);
    }
  }, []);

  const drawCaptcha = useCallback((code: string) => {
    const canvas = canvasRef.current;
    if (!canvas || !code) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Subtle luxury background gradient
    const gradient = ctx.createLinearGradient(
      0,
      0,
      canvas.width,
      canvas.height,
    );
    gradient.addColorStop(0, "#fbf8f5");
    gradient.addColorStop(1, "#ede6dc");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Decorative interference lines
    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = `rgba(180, 155, 125, ${Math.random() * 0.4 + 0.35})`;
      ctx.lineWidth = Math.random() * 1.5 + 1;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }

    // Noise dots
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(120, 100, 80, ${Math.random() * 0.35 + 0.15})`;
      ctx.beginPath();
      ctx.arc(
        Math.random() * canvas.width,
        Math.random() * canvas.height,
        Math.random() * 1.2 + 0.5,
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }

    // Render characters with slight rotation
    const charSpacing = canvas.width / (code.length + 1);
    const colors = ["#2a2520", "#5a6f3e", "#8b4513", "#4338ca", "#b45309"];
    for (let i = 0; i < code.length; i++) {
      const char = code[i];
      ctx.save();
      const x = (i + 0.8) * charSpacing;
      const y = canvas.height / 2 + (Math.random() * 4 - 2);
      ctx.translate(x, y);
      const angle = (Math.random() - 0.5) * 0.35;
      ctx.rotate(angle);
      ctx.font = `bold 18px "Cinzel", "Cormorant Garamond", Georgia, serif`;
      ctx.fillStyle = colors[i % colors.length];
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";
      ctx.fillText(char, 0, 0);
      ctx.restore();
    }
  }, []);

  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  useEffect(() => {
    if (captchaCode) {
      drawCaptcha(captchaCode);
    }
  }, [captchaCode, drawCaptcha]);

  const todayStr = new Date().toISOString().split("T")[0];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0];

  const validationSchema = Yup.object({
    FirstName: Yup.string().trim().required("Full name is required"),
    MobileNo: Yup.string()
      .trim()
      .matches(/^[0-9+\s-]{10,15}$/, "Enter a valid 10-digit phone number")
      .required("Mobile number is required"),
    Email: Yup.string()
      .trim()
      .email("Invalid email address")
      .required("Email is required"),
    Room: Yup.string()
      .trim()
      .required("No. of rooms is required")
      .test("no-zero-rooms", "No. of rooms must be at least 1", (val) => {
        if (!val) return false;
        const num = Number(val);
        return !isNaN(num) && num > 0;
      }),
    Adults: Yup.string()
      .trim()
      .required("No. of guests is required")
      .test("no-zero-guests", "No. of guests must be at least 1", (val) => {
        if (!val) return false;
        const num = Number(val);
        return !isNaN(num) && num > 0;
      }),
    CheckIn: Yup.string()
      .trim()
      .required("Check-in date is required")
      .test(
        "is-future-date",
        "Check-in date must be a future date",
        function (value) {
          if (!value) return false;
          const selected = new Date(value);
          selected.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return selected.getTime() > today.getTime();
        },
      ),
    CheckOut: Yup.string()
      .nullable()
      .test(
        "is-after-checkin",
        "Check-out date must be after check-in date",
        function (value) {
          const { CheckIn } = this.parent;
          if (!CheckIn || !value) return true;
          return new Date(value) > new Date(CheckIn);
        },
      ),
    Remarks: Yup.string(),
    CaptchaInput: Yup.string()
      .trim()
      .required("Security code is required")
      .test(
        "match-captcha",
        "Incorrect code. Please try again.",
        function (val) {
          return (
            !val ||
            !captchaCode ||
            val.toUpperCase() === captchaCode.toUpperCase()
          );
        },
      ),
  });

  const formik = useFormik<IOfferInquiryFormValues>({
    initialValues: {
      FirstName: "",
      MobileNo: "",
      Email: "",
      Room: "",
      Adults: "",
      CheckIn: "",
      CheckOut: "",
      Remarks: "",
      CaptchaInput: "",
      PropertyURL: typeof window !== "undefined" ? window.location.href : "",
    },
    validationSchema,
    enableReinitialize: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const searchParams =
          typeof window !== "undefined"
            ? parseQueryParams(window.location.search)
            : null;

        const effectiveUrl =
          values.PropertyURL ||
          (typeof window !== "undefined" ? window.location.href : "");

        const enrichedRemarks = [
          `Offer: ${offerTitle}${offerId ? ` (ID: ${offerId})` : ""}`,
          values.Room ? `Rooms: ${values.Room}` : null,
          values.Adults ? `Guests: ${values.Adults}` : null,
          values.CheckIn ? `Check-In: ${values.CheckIn}` : null,
          values.CheckOut ? `Check-Out: ${values.CheckOut}` : null,
          values.Remarks ? `Notes: ${values.Remarks}` : null,
        ]
          .filter(Boolean)
          .join(" | ");

        const payload: Record<string, any> = {
          FirstName: values.FirstName.trim(),
          MobileNo: values.MobileNo.trim(),
          Email: values.Email.trim(),
          CheckIn: values.CheckIn || null,
          CheckOut: values.CheckOut || null,
          Remarks: enrichedRemarks,
          InquiryType: `Offer - ${offerTitle}`,
          CheckInTime: "",
          DiningID: null,
          Room: values.Room ? `${values.Room}` : "",
          Adults: values.Adults ? `${values.Adults}` : "",
          PropertyURL: effectiveUrl,
          source: searchParams?.utm_source || "",
          medium: searchParams?.utm_medium || "",
          campaign: searchParams?.utm_campaign || "",
          utmContent: searchParams?.utm_content || "",
        };

        const res = await dispatch(insertInquiry(payload));

        if (res?.meta?.requestStatus === "fulfilled") {
          setIsSuccess(true);
          resetForm();
          generateCaptcha();
          // toast.success("Thank you! Your offer enquiry has been received.");
          setTimeout(() => {
            if (onClose) onClose();
            router.push("/thank-you");
          }, 1200);
        } else {
          generateCaptcha();
          const errMsg =
            (res as any)?.payload?.message ||
            (res as any)?.error?.message ||
            "Unable to submit inquiry. Please try again or call us directly.";
          toast.error(errMsg);
        }
      } catch (err: any) {
        generateCaptcha();
        toast.error(
          err?.message || "Something went wrong. Please try again later.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    formikRef.current = formik;
  }, [formik]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      formik.setFieldValue("PropertyURL", window.location.href);
    }
  }, []);

  // Calculate minimum checkout date based on selected CheckIn
  const minCheckOutDate = formik.values.CheckIn
    ? (() => {
        try {
          const nextDay = new Date(formik.values.CheckIn);
          nextDay.setDate(nextDay.getDate() + 1);
          return nextDay.toISOString().split("T")[0];
        } catch {
          return tomorrowStr;
        }
      })()
    : tomorrowStr;

  return (
    <div
      id="offer-inquiry-form"
      className={`bg-white/95 backdrop-blur-md text-[#2a2520] p-4 sm:p-5 md:p-6 rounded-2xl shadow-2xl border border-stone-200/90 w-full max-w-lg mx-auto relative overflow-hidden text-left ${className}`}
    >
      {/* Header Accent Strip matching Landing Page Banner */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#5a6f3e]" />

      {/* Form Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3
            className="text-xl sm:text-2xl font-normal text-[#2a2520] font-cormorant tracking-wide leading-snug"
            style={{ fontFamily: "var(--font-cormorant-family), serif" }}
          >
            {offerTitle ? `Enquire for ${offerTitle}` : "Special Offer Enquiry"}
          </h3>
          {/* <p
            className="text-xs text-[#2a2520]/70 font-jost mt-0.5"
            style={{ fontFamily: "var(--font-jost-family), sans-serif" }}
          >
            Inquire now for the best rates and exclusive offer benefits.
          </p> */}
        </div>

        {/* Modal Close Button (only displayed in modal mode) */}
        {isModal && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-stone-100 hover:bg-[#2a2520] hover:text-white text-stone-600 flex items-center justify-center transition-all duration-200 shrink-0 cursor-pointer"
            aria-label="Close modal"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isSuccess ? (
        <div className="py-10 text-center flex flex-col items-center justify-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center text-white mb-3 bg-[#5a6f3e]">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h4
            className="text-xl font-normal text-[#2a2520] font-cormorant mb-1"
            style={{ fontFamily: "var(--font-cormorant-family), serif" }}
          >
            Inquiry Submitted Successfully!
          </h4>
          <p
            className="text-xs text-[#6b6460] font-jost"
            style={{ fontFamily: "var(--font-jost-family), sans-serif" }}
          >
            Thank you! Our resort team will contact you promptly.
          </p>
        </div>
      ) : (
        <form
          onSubmit={formik.handleSubmit}
          className="space-y-2.5 sm:space-y-3"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
            {/* 1. Full Name */}
            <div className="sm:col-span-2">
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#554e48] mb-1">
                Full Name <span className="text-rose-600">*</span>
              </label>
              <div className="relative flex items-center">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f76] pointer-events-none" />
                <input
                  type="text"
                  name="FirstName"
                  placeholder="Full Name *"
                  value={formik.values.FirstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-stone-200 rounded-lg text-xs text-[#2a2520] placeholder-stone-400 outline-none transition-colors focus:border-[#5a6f3e]"
                />
              </div>
              {formik.touched.FirstName && formik.errors.FirstName && (
                <p className="text-[10px] text-rose-600 mt-0.5 ml-1">
                  {formik.errors.FirstName as string}
                </p>
              )}
            </div>

            {/* 2. Phone Number */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#554e48] mb-1">
                Phone Number <span className="text-rose-600">*</span>
              </label>
              <div className="relative flex items-center">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f76] pointer-events-none" />
                <input
                  type="tel"
                  name="MobileNo"
                  placeholder="Phone Number *"
                  value={formik.values.MobileNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-stone-200 rounded-lg text-xs text-[#2a2520] placeholder-stone-400 outline-none transition-colors focus:border-[#5a6f3e]"
                />
              </div>
              {formik.touched.MobileNo && formik.errors.MobileNo && (
                <p className="text-[10px] text-rose-600 mt-0.5 ml-1">
                  {formik.errors.MobileNo as string}
                </p>
              )}
            </div>

            {/* 3. Email Address */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#554e48] mb-1">
                Email Address <span className="text-rose-600">*</span>
              </label>
              <div className="relative flex items-center">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f76] pointer-events-none" />
                <input
                  type="email"
                  name="Email"
                  placeholder="Email Address *"
                  value={formik.values.Email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-stone-200 rounded-lg text-xs text-[#2a2520] placeholder-stone-400 outline-none transition-colors focus:border-[#5a6f3e]"
                />
              </div>
              {formik.touched.Email && formik.errors.Email && (
                <p className="text-[10px] text-rose-600 mt-0.5 ml-1">
                  {formik.errors.Email as string}
                </p>
              )}
            </div>

            {/* 4. Check-In Date */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#554e48] mb-1">
                Check-In Date <span className="text-rose-600">*</span>
              </label>
              <div className="relative flex items-center">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f76] pointer-events-none" />
                <input
                  type="date"
                  name="CheckIn"
                  min={tomorrowStr}
                  value={formik.values.CheckIn}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onClick={(e) => (e.currentTarget as any).showPicker?.()}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-stone-200 rounded-lg text-xs text-[#2a2520] outline-none transition-colors focus:border-[#5a6f3e] cursor-pointer"
                />
              </div>
              {formik.touched.CheckIn && formik.errors.CheckIn && (
                <p className="text-[10px] text-rose-600 mt-0.5 ml-1">
                  {formik.errors.CheckIn as string}
                </p>
              )}
            </div>

            {/* 5. Check-Out Date */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#554e48] mb-1">
                Check-Out Date
              </label>
              <div className="relative flex items-center">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f76] pointer-events-none" />
                <input
                  type="date"
                  name="CheckOut"
                  min={minCheckOutDate}
                  value={formik.values.CheckOut}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onClick={(e) => (e.currentTarget as any).showPicker?.()}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-stone-200 rounded-lg text-xs text-[#2a2520] outline-none transition-colors focus:border-[#5a6f3e] cursor-pointer"
                />
              </div>
              {formik.touched.CheckOut && formik.errors.CheckOut && (
                <p className="text-[10px] text-rose-600 mt-0.5 ml-1">
                  {formik.errors.CheckOut as string}
                </p>
              )}
            </div>

            {/* 6. No. Of Rooms */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#554e48] mb-1">
                No. Of Rooms <span className="text-rose-600">*</span>
              </label>
              <div className="relative flex items-center">
                <BedDouble className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f76] pointer-events-none" />
                <input
                  type="number"
                  name="Room"
                  min="1"
                  placeholder="No. of Rooms *"
                  value={formik.values.Room}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") e.preventDefault();
                  }}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-stone-200 rounded-lg text-xs text-[#2a2520] placeholder-stone-400 outline-none transition-colors focus:border-[#5a6f3e]"
                />
              </div>
              {formik.touched.Room && formik.errors.Room && (
                <p className="text-[10px] text-rose-600 mt-0.5 ml-1">
                  {formik.errors.Room as string}
                </p>
              )}
            </div>

            {/* 7. No. Of Guests */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#554e48] mb-1">
                No. Of Guests <span className="text-rose-600">*</span>
              </label>
              <div className="relative flex items-center">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f76] pointer-events-none" />
                <input
                  type="number"
                  name="Adults"
                  min="1"
                  placeholder="No. of Guests *"
                  value={formik.values.Adults}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") e.preventDefault();
                  }}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-stone-200 rounded-lg text-xs text-[#2a2520] placeholder-stone-400 outline-none transition-colors focus:border-[#5a6f3e]"
                />
              </div>
              {formik.touched.Adults && formik.errors.Adults && (
                <p className="text-[10px] text-rose-600 mt-0.5 ml-1">
                  {formik.errors.Adults as string}
                </p>
              )}
            </div>

            {/* 8. Remarks / Special Request */}
            <div className="sm:col-span-2">
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#554e48] mb-1">
                Special Request / Notes
              </label>
              <div className="relative flex items-start">
                <MessageSquare className="absolute left-3 top-2.5 w-4 h-4 text-[#8a7f76] pointer-events-none" />
                <textarea
                  name="Remarks"
                  rows={2}
                  placeholder="Any special requirements, preferred room view, celebration notes..."
                  value={formik.values.Remarks}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-stone-200 rounded-lg text-xs text-[#2a2520] placeholder-stone-400 outline-none transition-colors focus:border-[#5a6f3e] resize-none"
                />
              </div>
              {formik.touched.Remarks && formik.errors.Remarks && (
                <p className="text-[10px] text-rose-600 mt-0.5 ml-1">
                  {formik.errors.Remarks as string}
                </p>
              )}
            </div>

            {/* 9. Security Verification / Captcha */}
            <div className="sm:col-span-2 bg-[#f9f7f4] border border-stone-200 rounded-lg p-2.5">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#554e48]">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#5a6f3e]" />
                  <span>Human Verification *</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="rounded border border-stone-300 overflow-hidden bg-[#f5f1ec]">
                    <canvas
                      ref={canvasRef}
                      width={110}
                      height={32}
                      className="select-none pointer-events-none block"
                      title="Security Code"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={generateCaptcha}
                    title="Refresh code"
                    className="p-1 rounded border border-stone-200 bg-white hover:bg-stone-100 text-[#6b6460] transition-colors cursor-pointer group"
                  >
                    <RotateCw className="w-3.5 h-3.5 transition-transform group-hover:rotate-180 duration-500" />
                  </button>
                </div>
              </div>
              <input
                type="text"
                name="CaptchaInput"
                maxLength={6}
                autoComplete="off"
                placeholder="Enter 5 characters shown above"
                value={formik.values.CaptchaInput}
                onChange={(e) => {
                  formik.setFieldValue(
                    "CaptchaInput",
                    e.target.value.toUpperCase(),
                  );
                }}
                onBlur={formik.handleBlur}
                className="w-full px-2.5 py-1.5 bg-white border border-stone-300 rounded text-xs font-semibold tracking-widest uppercase text-[#2a2520] placeholder:normal-case placeholder:font-normal placeholder:tracking-normal placeholder-stone-400 outline-none focus:border-[#5a6f3e]"
              />
              {formik.touched.CaptchaInput && formik.errors.CaptchaInput && (
                <p className="text-[10px] text-rose-600 mt-0.5 ml-1">
                  {formik.errors.CaptchaInput as string}
                </p>
              )}
            </div>

            {/* 10. Submit Button */}
            <div className="sm:col-span-2 pt-1">
              <button
                type="submit"
                disabled={formik.isSubmitting}
                className="w-full py-2.5 px-4 rounded-lg text-white text-xs font-semibold uppercase tracking-wider flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all cursor-pointer disabled:opacity-70 bg-[#2a2520] hover:bg-[#5a6f3e]"
              >
                <span>
                  {formik.isSubmitting
                    ? "Submitting..."
                    : "Submit Offer Inquiry"}
                </span>
                <Send className="w-3.5 h-3.5 opacity-90" />
              </button>
            </div>

            {/* Privacy Note */}
            {/* <p
              className="sm:col-span-2 text-[10px] text-center text-[#8a7f76] pt-1"
              style={{ fontFamily: "var(--font-jost-family), sans-serif" }}
            >
              🔒 We respect your privacy. Our reservation team will reach out to
              you promptly.
            </p> */}
          </div>
        </form>
      )}
    </div>
  );
};

export default OfferInquiryForm;
